import { Controller } from '@hotwired/stimulus';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const LAST_TIME = '23:30';

// "HH:MM" every 30 minutes, 00:00 → 23:30.
const TIMES = Array.from({ length: 48 }, (_, i) => {
    const hours = String(Math.floor(i / 2)).padStart(2, '0');

    return `${hours}:${i % 2 ? '30' : '00'}`;
});

const addMinutes = (time, minutes) => {
    const [hours, mins] = time.split(':').map(Number);
    const total = Math.min(hours * 60 + mins + minutes, 23 * 60 + 30);

    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const pushEvent = (action, label = '') => {
    dataLayer.push({
        event: 'dataLayer.gaEvent',
        gaEvent: { category: 'Account', action, label },
    });
};

/*
 * DDT-4157: the « Horaires de disponibilité » section. The week is kept here as
 * { 1: { active, slots: [{ start, end }] }, …, 7: … } (1 = Monday), rendered from the
 * row <template>, and saved whole on every change. A change that leaves an error is
 * shown but not saved; the server runs the same checks.
 */
/* stimulusFetch: 'lazy' */
export default class extends Controller {
    static values = {
        schedule: Object,
        saveUrl: String,
        dayLabels: Array,
        errorMessages: Object,
    };

    static targets = ['days', 'row', 'error'];

    connect() {
        // A Stimulus Object value is parsed again on every read, so edits are made on a copy.
        this.schedule = this.scheduleValue;
        this.saving = Promise.resolve();
        this.render();
    }

    render(errorDay = null) {
        this.daysTarget.replaceChildren();

        for (let day = 1; day <= 7; day += 1) {
            const { active, slots } = this.schedule[day];

            slots.forEach((slot, index) => {
                const row = this.rowTarget.content.firstElementChild.cloneNode(true);
                const isFirst = 0 === index;
                const isLast = slots.length - 1 === index;

                const toggle = row.querySelector('[data-role="switch"]');
                toggle.checked = active;
                toggle.id = `online-consult-day-${day}`;
                toggle.dataset.day = day;
                // Later lines keep the switch and day space on desktop so the times line up,
                // and drop it on mobile, where the times sit under the day.
                const head = row.querySelector('[data-role="day-head"]');
                ['invisible', 'd-none', 'd-md-flex'].forEach((name) => head.classList.toggle(name, !isFirst));
                // Figma mobile: 16px between days, 8px between the lines of a day.
                row.classList.toggle('mt-1', isFirst && day > 1);
                row.classList.toggle('mt-md-0', isFirst && day > 1);

                const label = row.querySelector('[data-role="day"]');
                label.textContent = this.dayLabelsValue[day - 1];
                // Figma: an on day is a navy 10% pill, an off day a white one with a grey border.
                ['bg-primary-dark', 'bg-opacity-10', 'text-primary-dark'].forEach((name) => label.classList.toggle(name, active));
                ['bg-white', 'border', 'border-light-grey', 'online-consult-schedule-day--off'].forEach((name) => label.classList.toggle(name, !active));

                ['start', 'end'].forEach((field) => {
                    const select = row.querySelector(`[data-role="${field}"]`);
                    select.append(...TIMES.map((time) => new Option(time, time, false, time === slot[field])));
                    select.disabled = !active;
                    select.dataset.day = day;
                    select.dataset.index = index;
                    select.dataset.field = field;
                    // Set before the row is inserted: Tom Select reads the options,
                    // the selection and the disabled state when it connects.
                });

                const deleteButton = row.querySelector('[data-role="delete"]');
                deleteButton.dataset.day = day;
                deleteButton.dataset.index = index;
                // The last line of a day cannot be removed.
                deleteButton.classList.toggle('invisible', 1 === slots.length);
                deleteButton.disabled = !active;

                const addButton = row.querySelector('[data-role="add"]');
                addButton.dataset.day = day;
                addButton.classList.toggle('invisible', !isLast);
                // No room after a line ending at 23:30: a new line would be 23:30–23:30.
                addButton.disabled = !active || LAST_TIME === slot.end;

                this.daysTarget.append(row);
            });
        }

        this.markErrorDay(errorDay);
    }

    // The global .tomselected.is-invalid rule turns the Tom Select field red.
    markErrorDay(errorDay) {
        this.daysTarget.querySelectorAll('select[data-day]').forEach((select) => {
            select.classList.toggle('is-invalid', Number(select.dataset.day) === errorDay);
        });
    }

    toggleDay(event) {
        const day = Number(event.target.dataset.day);
        const active = event.target.checked;

        this.schedule[day].active = active;
        pushEvent('onlineconsult_day_toggle', `${DAY_KEYS[day - 1]} | ${active ? 'on' : 'off'}`);
        this.changed();
    }

    changeTime(event) {
        const { day, index, field } = event.target.dataset;

        this.schedule[day].slots[index][field] = event.target.value;

        // The + sits in the same row as the times; keep its 23:30 rule in step.
        const addButton = event.target.parentElement.querySelector('[data-role="add"]');

        if ('end' === field && addButton) {
            addButton.disabled = !this.schedule[day].active || LAST_TIME === event.target.value;
        }
        // No re-render: the rows stay the same, and rebuilding them would destroy the
        // Tom Select the lawyer is using.
        this.changed(false);
    }

    addSlot(event) {
        const day = Number(event.currentTarget.dataset.day);
        const { slots } = this.schedule[day];
        const start = slots[slots.length - 1].end;

        if (LAST_TIME === start) {
            return;
        }

        slots.push({ start, end: addMinutes(start, 60) });
        pushEvent('onlineconsult_slot_add');
        this.changed();
    }

    deleteSlot(event) {
        const { day, index } = event.currentTarget.dataset;
        const { slots } = this.schedule[day];

        if (slots.length < 2) {
            return;
        }

        slots.splice(Number(index), 1);
        pushEvent('onlineconsult_slot_delete');
        this.changed();
    }

    changed(rowsChanged = true) {
        const error = this.findError();
        const errorDay = error ? error.day : null;

        if (rowsChanged) {
            this.render(errorDay);
        } else {
            this.markErrorDay(errorDay);
        }

        this.showError(error ? error.code : null);

        if (error) {
            pushEvent('onlineconsult_schedule_error', error.code);

            return;
        }

        // Saves run one after the other, so a slow request can never land after a
        // newer one and put back an older week.
        const schedule = structuredClone(this.schedule);
        this.saving = this.saving.then(() => this.save(schedule));
    }

    // Same rules, in the same order, as OnlineConsultationScheduleService::findError().
    findError() {
        for (let day = 1; day <= 7; day += 1) {
            if (this.schedule[day].slots.some(({ start, end }) => end <= start)) {
                return { code: 'end_before_start', day };
            }
        }

        for (let day = 1; day <= 7; day += 1) {
            const slots = [...this.schedule[day].slots].sort((a, b) => a.start.localeCompare(b.start));

            for (let i = 1; i < slots.length; i += 1) {
                if (slots[i].start < slots[i - 1].end) {
                    return { code: 'overlap', day };
                }
            }
        }

        return null;
    }

    async save(schedule) {
        try {
            const response = await fetch(this.saveUrlValue, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ days: schedule }),
            });
            const result = await response.json();

            if (!result.status) {
                this.markErrorDay(result.day ?? null);
                this.showError(result.error ?? 'unknown');

                return;
            }

            // The go-online pop-up recaps the hours: swap in the server-rendered recap.
            const popupSummary = document.getElementById('online-consultation-schedule-summary');

            if (popupSummary && result.summary_html) {
                popupSummary.innerHTML = result.summary_html;
            }

            // Saving the hours lifts a suspension (server side too).
            document.dispatchEvent(new CustomEvent('online-consultation:schedule-resumed'));

            const activeDays = Object.values(schedule).filter((day) => day.active);
            const activeSlots = activeDays.reduce((total, day) => total + day.slots.length, 0);

            pushEvent('onlineconsult_schedule_save', `${activeDays.length} | ${activeSlots}`);
        } catch (error) {
            this.showError('unknown');
        }
    }

    showError(code) {
        this.errorTarget.textContent = code ? (this.errorMessagesValue[code] ?? this.errorMessagesValue.unknown) : '';
        this.errorTarget.classList.toggle('d-none', !code);
    }
}
